import React, {useState, useRef, useCallback, useEffect} from 'react';
import {Text} from '../Text';
import {cn} from '@/utils/common_functions';

interface JsonViewerProps {
  value: any;
  children: React.ReactNode;
  className?: string;
}

export const JsonViewer: React.FC<JsonViewerProps> = ({value, children, className = ''}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const parseValue = useCallback((val: any): any => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }, []);

  const formatJson = useCallback((val: any): string => {
    const parsed = parseValue(val);
    if (parsed === null || parsed === undefined) return 'null';
    if (typeof parsed === 'string') return parsed;
    return JSON.stringify(parsed, null, 2);
  }, [parseValue]);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const formattedValue = formatJson(value);

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {children}
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute z-[999] top-[calc(100%+4px)] left-0 p-4 bg-white border-2 border-ui_input_border rounded-lg shadow-lg min-w-[400px] max-w-[600px] max-h-[500px] overflow-auto"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <pre className="bg-ui_sky_blue p-3 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words">
            {formattedValue}
          </pre>
        </div>
      )}
    </div>
  );
};
